// frontend/dispatcher-client/src/components/GlobalNotificationListener.js
import { useCallback, useEffect, useRef } from "react";
import api from "../axiosConfig";
import { getSocket } from "../lib/socket";

const NOTIF_SOUND_SRC = "/sounds/notification.mp3";
const SOUND_DEDUPE_MS = 900;

function getNotifsActive() {
  const v = window.localStorage.getItem("app.notifications");
  return v ? v === "active" : true;
}

function getNotifId(notif) {
  if (!notif || typeof notif !== "object") return null;
  return notif.id != null ? String(notif.id) : null;
}

function GlobalNotificationListener() {
  const notificationSoundRef = useRef(null);
  const soundUnlockedRef = useRef(false);
  const knownNotificationIdsRef = useRef(new Set());
  const hasLoadedInitialNotificationsRef = useRef(false);

  // ✅ evita som duplicado quando chegam "new" + "stale" quase ao mesmo tempo
  const lastSoundAtRef = useRef(0);
  const playedNotificationIdsRef = useRef(new Set());

  useEffect(() => {
    notificationSoundRef.current = new Audio(NOTIF_SOUND_SRC);
    notificationSoundRef.current.volume = 0.55;
    notificationSoundRef.current.preload = "auto";

    const unlockSound = () => {
      if (!notificationSoundRef.current || soundUnlockedRef.current) return;

      notificationSoundRef.current
        .play()
        .then(() => {
          notificationSoundRef.current.pause();
          notificationSoundRef.current.currentTime = 0;
          soundUnlockedRef.current = true;

          console.log("[GlobalNotificationListener] notification sound unlocked");
        })
        .catch(() => {
          // Browser ainda pode bloquear até haver interação real.
        });
    };

    window.addEventListener("click", unlockSound);
    window.addEventListener("keydown", unlockSound);
    window.addEventListener("touchstart", unlockSound);

    return () => {
      window.removeEventListener("click", unlockSound);
      window.removeEventListener("keydown", unlockSound);
      window.removeEventListener("touchstart", unlockSound);
    };
  }, []);

  const playNotificationSound = useCallback((options = {}) => {
    if (!getNotifsActive()) return;

    const audio = notificationSoundRef.current;
    if (!audio) {
      console.warn("[GlobalNotificationListener] no audio ref available");
      return;
    }

    const notif = options.notification || null;
    const reason = options.reason || "unknown";
    const notifId = getNotifId(notif);

    try {
      const now = Date.now();

      // ✅ Se já tocou para este id, não toca outra vez.
      if (notifId && playedNotificationIdsRef.current.has(notifId)) {
        console.log("[GlobalNotificationListener] sound skipped duplicate id", {
          notifId,
          reason,
        });
        return;
      }

    // ✅ Evita duplo som apenas para eventos sem ID.
    // Para notificações com ID, o dedupe correto é playedNotificationIdsRef.
    if (!notifId && now - lastSoundAtRef.current < SOUND_DEDUPE_MS) {
    console.log("[GlobalNotificationListener] sound skipped throttle", {
        notifId,
        reason,
    });

    return;
    }

      lastSoundAtRef.current = now;

      if (notifId) {
        playedNotificationIdsRef.current.add(notifId);
      }

      audio.currentTime = 0;

      console.log("[GlobalNotificationListener] 🔊 playing notification sound", {
        notifId,
        reason,
        type: notif?.notificationType || null,
      });

      audio.play().catch((e) => {
        console.warn("[GlobalNotificationListener] sound blocked/failed", {
          reason,
          notifId,
          message: e?.message,
        });
      });
    } catch (e) {
      console.warn("[GlobalNotificationListener] sound failed", {
        reason,
        notifId,
        message: e?.message,
      });
    }
  }, []);

  const refreshNotificationsAndDetectNew = useCallback(async () => {
    if (!getNotifsActive()) return;

    try {
      const { data } = await api.get("/notifications4dispatcher", {
        params: {
          limit: 500,
          offset: 0,
        },
      });

      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      const previousIds = knownNotificationIdsRef.current || new Set();

      const newItems = items.filter((item) => {
        const id = getNotifId(item);
        if (!id) return false;
        return !previousIds.has(id);
      });

      knownNotificationIdsRef.current = new Set(
        items
          .filter((item) => item?.id)
          .map((item) => String(item.id))
      );

      try {
        const unread = items.filter((n) => !n.readAt).length;

        window.dispatchEvent(
          new CustomEvent("dispatcher:notifs:unreadCount", {
            detail: { unread },
          })
        );
      } catch (_) {}

      if (
        hasLoadedInitialNotificationsRef.current &&
        newItems.length > 0
      ) {
        console.log(
          "[GlobalNotificationListener] new notification detected by refetch",
          newItems[0]
        );

        playNotificationSound({
          notification: newItems[0],
          reason: "refetch-detected-new",
        });
      }

      hasLoadedInitialNotificationsRef.current = true;
    } catch (e) {
      console.warn(
        "[GlobalNotificationListener] failed to refresh notifications",
        e
      );
    }
  }, [playNotificationSound]);

  useEffect(() => {
    refreshNotificationsAndDetectNew();
  }, [refreshNotificationsAndDetectNew]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const subscribe = () => {
      if (socket.connected) {
        socket.emit("subscribe:loads");
        console.log("[GlobalNotificationListener] subscribed to loads notifications");
      }
    };

    const handleNewNotification = (notif) => {
      console.log(
        "[GlobalNotificationListener] dispatcher:notifications:new recebido",
        notif
    );

    if (!notif || typeof notif !== "object") return;

    const id = getNotifId(notif);

    // ✅ Mantém o known atualizado para o refetch,
    // mas NUNCA uses isto para bloquear o som.
    if (id) {
        knownNotificationIdsRef.current.add(id);
    }

    // ✅ Se chegou evento real "dispatcher:notifications:new",
    // toca som. O dedupe fica apenas em playedNotificationIdsRef.
    playNotificationSound({
        notification: notif,
        reason: id ? "socket-new" : "socket-new-no-id",
    });

    refreshNotificationsAndDetectNew();
    };

    const handleStale = () => {
      console.log(
        "[GlobalNotificationListener] dispatcher:notifications:stale recebido"
      );

      refreshNotificationsAndDetectNew();
    };

    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);

    if (socket.connected) {
      subscribe();
    }

    socket.on("dispatcher:notifications:new", handleNewNotification);
    socket.on("dispatcher:notifications:stale", handleStale);

    return () => {
      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);
      socket.off("dispatcher:notifications:new", handleNewNotification);
      socket.off("dispatcher:notifications:stale", handleStale);
    };
  }, [playNotificationSound, refreshNotificationsAndDetectNew]);

  /**
   * ✅ Fallback extra:
   * Qualquer componente pode avisar este listener que recebeu uma notificação nova.
   * Assim, se a página de notificações receber o socket mas este listener não apanhar
   * no timing certo, ainda tocamos som.
   */
  useEffect(() => {
    const handleFrontendNotificationEvent = (event) => {
      const notif = event?.detail?.notification || null;

      console.log(
        "[GlobalNotificationListener] frontend custom notification event recebido",
        notif
      );

      if (!notif || typeof notif !== "object") return;

      playNotificationSound({
        notification: notif,
        reason: "frontend-custom-event",
      });
    };

    window.addEventListener(
      "loadevo:dispatcher-notification:new",
      handleFrontendNotificationEvent
    );

    return () => {
      window.removeEventListener(
        "loadevo:dispatcher-notification:new",
        handleFrontendNotificationEvent
      );
    };
  }, [playNotificationSound]);

  return null;
}

export default GlobalNotificationListener;